import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    GetVrList,
    AddVrItem,
    UpdateVrItem,
} from "../../Redux/Actions/homeVrActions";
import { columnsHomeVrList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import HomeVrEditModal from "./addAndEditHomeVR";
import HomeVrViewModal from "./viewHomeVR";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Button } from "antd";

const ListHomeVr = () => {
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVr, setSelectedVr] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedViewVr, setSelectedViewVr] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const vrState = useSelector((state) => state.vrHome || {});
    const vrItems = useMemo(() => vrState.vrItems || [], [vrState.vrItems]);
    const vrTotal = useMemo(() => vrState.total || 0, [vrState.total]);
    const { loading } = vrState;

    const fetchDetails = useCallback(
        (showLoader = true, page = pagination.current, pageSize = pagination.pageSize) => {
            const payload = {
                page,
                limit: pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            return dispatch(GetVrList(payload)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch VR list");
                }
            });
        },
        [dispatch, debouncedSearch, dateRange, pagination.current, pagination.pageSize]
    );

    useEffect(() => {
        fetchDetails(true, pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch, dateRange]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: vrTotal }));
    }, [vrTotal]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const openEditModal = useCallback((vr = null) => {
        setSelectedVr(vr);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedVr(null);
        setIsModalOpen(false);
    };

    const openViewModal = useCallback((vr = null) => {
        setSelectedViewVr(vr);
        setIsViewModalOpen(true);
    }, []);

    const closeViewModal = () => {
        setSelectedViewVr(null);
        setIsViewModalOpen(false);
    };

    const handleSubmit = async (values) => {
        try {
            let result;
            if (selectedVr?._id) {
                result = await dispatch(UpdateVrItem({ ...values, id: selectedVr._id }));
            } else {
                result = await dispatch(AddVrItem(values));
            }

            if (result) {
                await fetchDetails(false, pagination.current, pagination.pageSize);
                closeEditModal();
                toast.success(selectedVr?._id ? "VR item updated successfully" : "VR item added successfully");
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save VR item");
            return { success: false };
        }
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "88.5%" }}>
                {canAdd && (
                    <Button
                        style={{ background: "#070C18", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={() => openEditModal(null)}
                    >
                        Add VR
                    </Button>
                )}
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={vrItems}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                onChange={handleTableChange}
                h3heading="Home VR"
                columns={columnsHomeVrList(
                    pagination.current,
                    pagination.pageSize,
                    openViewModal,
                    openEditModal,
                    canEdit
                )}
                loading={loading}
            />

            {isModalOpen && (
                <HomeVrEditModal
                    visible={isModalOpen}
                    vrData={selectedVr}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}

            {isViewModalOpen && (
                <HomeVrViewModal
                    visible={isViewModalOpen}
                    vrData={selectedViewVr}
                    onClose={closeViewModal}
                />
            )}

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListHomeVr;